import { useCallback, useEffect, useRef } from "react";
import {
  scrollToFirstInvalidField,
  scrollToValidationError
} from "../utilities/scrollUtilities";

/**
 * Options for delayed validation error scrolling.
 */
export interface UseDelayedScrollOptions {
  /**
   * Delay before scrolling (ms).
   * @default 200
   */
  delay?: number;

  /**
   * Scroll offset from top (px).
   * @default 180
   */
  offset?: number;

  /**
   * Scroll behavior.
   * @default 'smooth'
   */
  behavior?: ScrollBehavior;

  /**
   * CSS selector for validation error elements.
   * @default '.validation-error'
   */
  errorSelector?: string;

  /**
   * CSS selector for form container.
   * @default 'form'
   */
  formSelector?: string;
}

/**
 * Return type for useDelayedScroll hook.
 */
export interface UseDelayedScrollReturn {
  /**
   * Scrolls to validation error after delay.
   */
  scrollToError: () => void;

  /**
   * Scrolls to first invalid field after delay.
   */
  scrollToInvalidField: () => void;

  /**
   * Cancels any pending scroll operation.
   */
  cancelScroll: () => void;
}

/**
 * Hook for managing delayed scrolling to validation errors.
 *
 * Provides functions that trigger scrolling after a configurable delay,
 * useful for coordinating with animations, DOM updates, or other async operations.
 *
 * **Features:**
 * - Configurable delay before scrolling
 * - Can scroll to validation errors OR invalid fields
 * - Cancellable (prevents scroll if component unmounts)
 * - Automatic cleanup on unmount
 * - Reusable across components
 *
 * **Use Cases:**
 * - Form submission with validation
 * - Multi-step forms
 * - Async validation scenarios
 * - Coordinating with animations
 *
 * @param options Configuration for scroll behavior and delay
 *
 * @example
 * ```tsx
 * const { scrollToError, scrollToInvalidField, cancelScroll } = useDelayedScroll({
 *   delay: 300,
 *   offset: 180,
 *   behavior: 'smooth',
 *   errorSelector: '.validation-error'
 * });
 *
 * const handleSubmit = async (e) => {
 *   e.preventDefault();
 *   const isValid = await validateForm();
 *
 *   if (!isValid) {
 *     scrollToError(); // Scroll after delay
 *   }
 * };
 * ```
 *
 * @example
 * ```tsx
 * // Quick validation scroll with defaults
 * const { scrollToError } = useDelayedScroll();
 *
 * if (!isFormValid) {
 *   scrollToError();
 * }
 * ```
 */
export function useDelayedScroll(
  options: UseDelayedScrollOptions = {}
): UseDelayedScrollReturn {
  const {
    delay = 200,
    offset = 180,
    behavior = "smooth",
    errorSelector = ".validation-error",
    formSelector = "form"
  } = options;

  // Track timeout IDs for cleanup
  const timeoutIdRef = useRef<number | undefined>(undefined);

  /**
   * Scrolls to validation error after configured delay.
   */
  const scrollToError = useCallback(() => {
    // Clear any existing timeout
    if (timeoutIdRef.current !== undefined) {
      clearTimeout(timeoutIdRef.current);
    }

    // Schedule scroll
    timeoutIdRef.current = window.setTimeout(() => {
      scrollToValidationError(errorSelector, offset, behavior);
      timeoutIdRef.current = undefined;
    }, delay);
  }, [delay, offset, behavior, errorSelector]);

  /**
   * Scrolls to first invalid field after configured delay.
   */
  const scrollToInvalidField = useCallback(() => {
    // Clear any existing timeout
    if (timeoutIdRef.current !== undefined) {
      clearTimeout(timeoutIdRef.current);
    }

    // Schedule scroll
    timeoutIdRef.current = window.setTimeout(() => {
      scrollToFirstInvalidField(formSelector, offset, behavior);
      timeoutIdRef.current = undefined;
    }, delay);
  }, [delay, offset, behavior, formSelector]);

  /**
   * Cancels any pending scroll operation.
   */
  const cancelScroll = useCallback(() => {
    if (timeoutIdRef.current !== undefined) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = undefined;
    }
  }, []);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (timeoutIdRef.current !== undefined) {
        clearTimeout(timeoutIdRef.current);
      }
    },
    []
  );

  return {
    scrollToError,
    scrollToInvalidField,
    cancelScroll
  };
}
